import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaPlus, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface GradingScheme {
  id?: number;
  min_score: number | string;
  max_score: number | string;
  grade: string;
  points: number | string;
  remark?: string;
}

const GradingSystems: React.FC = () => {
  const [gradingSchemes, setGradingSchemes] = useState<GradingScheme[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New row state
  const [newRows, setNewRows] = useState<GradingScheme[]>([]);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let c of cookieArray) {
      c = c.trim();
      if (c.indexOf(cookieName) === 0) return c.substring(cookieName.length, c.length);
    }
    return '';
  };

  const fetchGradingSchemes = useCallback(async () => {
    setLoading(true);
    try {
      const token = getCookie('token');
      const res = await axios.get("/exams/grading-systems/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter for those with NO subject (universal)
      const universal = res.data.filter((g: any) => g.subject === null);
      setGradingSchemes(universal);
    } catch (err) {
      toast.error("Failed to load grading schemes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGradingSchemes();
  }, [fetchGradingSchemes]);

  const handleAddRow = () => {
    setNewRows([...newRows, { min_score: "", max_score: "", grade: "", points: "", remark: "" }]);
  };

  const handleRowChange = (index: number, field: string, value: string) => {
    const updated = [...newRows];
    updated[index] = { ...updated[index], [field]: value };
    setNewRows(updated);
  };

  const handleDeleteRow = (index: number) => {
    const updated = [...newRows];
    updated.splice(index, 1);
    setNewRows(updated);
  };

  const handleDeleteExisting = async (id: number) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const token = getCookie('token');
      await axios.delete(`/exams/grading-systems/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGradingSchemes(prev => prev.filter(g => g.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleSave = async () => {
    if (newRows.length === 0) return;
    setSaving(true);
    try {
      const token = getCookie('token');
      const payload = newRows.map(row => ({
        ...row,
        subject: null, // Universal
        min_score: parseInt(row.min_score as string),
        max_score: parseInt(row.max_score as string),
        points: parseInt(row.points as string)
      }));

      await axios.post("/exams/grading-systems/bulk_create/", payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Saved successfully!");
      setNewRows([]);
      fetchGradingSchemes();
    } catch (err) {
      toast.error("Failed to save grading schemes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-content">
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Grading System Configuration</h2>
          <p className="text-secondary mb-0">Define the universal grading scale (e.g., 0-100)</p>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">

        {/* Existing Schemes */}
        <h5 className="fw-bold mb-3 border-bottom pb-2">Current Grading Scale</h5>
        {loading ? <div className="spinner-border text-primary"></div> : (
          <div className="table-responsive mb-4">
            <table className="table table-hover align-middle">
              <thead className="bg-light">
                <tr>
                  <th>Grade</th>
                  <th>Range</th>
                  <th>Points</th>
                  <th>Remark</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gradingSchemes.map(g => (
                  <tr key={g.id}>
                    <td className="fw-bold text-primary">{g.grade}</td>
                    <td>{g.min_score} - {g.max_score}</td>
                    <td>{g.points}</td>
                    <td>{g.remark || '-'}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-light text-danger" onClick={() => g.id && handleDeleteExisting(g.id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
                {gradingSchemes.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">No specific grading scale defined.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Add New Section */}
        <h5 className="fw-bold mb-3 text-secondary">Add New Grades</h5>
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr className="small text-muted text-uppercase">
                <th>Min Score</th>
                <th>Max Score</th>
                <th>Grade</th>
                <th>Points</th>
                <th>Remark</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {newRows.map((row, idx) => (
                <tr key={idx}>
                  <td><input type="number" className="form-control form-control-sm" placeholder="e.g. 80" value={row.min_score} onChange={e => handleRowChange(idx, 'min_score', e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" placeholder="e.g. 100" value={row.max_score} onChange={e => handleRowChange(idx, 'max_score', e.target.value)} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="A" maxLength={2} value={row.grade} onChange={e => handleRowChange(idx, 'grade', e.target.value)} /></td>
                  <td><input type="number" className="form-control form-control-sm" placeholder="12" value={row.points} onChange={e => handleRowChange(idx, 'points', e.target.value)} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Excellent" value={row.remark} onChange={e => handleRowChange(idx, 'remark', e.target.value)} /></td>
                  <td><button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRow(idx)}><FaTrash /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex gap-2 mt-3">
          <button className="btn btn-outline-primary" onClick={handleAddRow}><FaPlus className="me-2" /> Add Range</button>
          {newRows.length > 0 && (
            <button className="btn btn-success" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Configuration'}</button>
          )}
        </div>

      </div>
    </div>
  );
};

export default GradingSystems;
